import { Injectable } from '@angular/core';
import { CryptoService } from './crypto.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {
  private readonly TOKEN_KEY = 'auth_token';
  private readonly USER_KEY = 'user_data';
  private readonly REMEMBER_ME_KEY = 'remember_me';
  private readonly SAVED_EMAIL_KEY = 'saved_email';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly TRUSTED_DEVICE_KEY = 'trusted_device_token';

  constructor(private readonly cryptoService: CryptoService) {}

  // ==================== REMEMBER ME (AGORA CRIPTOGRAFADO) ====================

  get rememberMe(): boolean {
    const encryptedValue = localStorage.getItem(this.REMEMBER_ME_KEY);
    if (encryptedValue) {
      // Compatibilidade com formato antigo (plain text)
      if (encryptedValue === 'true') {
        // Migrar para formato criptografado (sem recursão)
        const encrypted = this.cryptoService.encrypt('true');
        localStorage.setItem(this.REMEMBER_ME_KEY, encrypted);
        return true;
      }
      if (encryptedValue === 'false') {
        localStorage.removeItem(this.REMEMBER_ME_KEY);
        return false;
      }
      const decrypted = this.cryptoService.decrypt(encryptedValue);
      if (!decrypted) {
        // Dados incompatíveis (chave diferente) - limpar
        if (environment.enableDebugLogs) {
          console.warn('[LocalStorageService] rememberMe corrompido, limpando...');
        }
        localStorage.removeItem(this.REMEMBER_ME_KEY);
        return false;
      }
      return decrypted === 'true';
    }
    return false;
  }

  set rememberMe(value: boolean) {
    if (value) {
      const encrypted = this.cryptoService.encrypt('true');
      localStorage.setItem(this.REMEMBER_ME_KEY, encrypted);
    } else {
      localStorage.removeItem(this.REMEMBER_ME_KEY);
      localStorage.removeItem(this.SAVED_EMAIL_KEY);
    }
  }

  // ==================== SAVED EMAIL ====================

  get savedEmail(): string {
    const encryptedEmail = localStorage.getItem(this.SAVED_EMAIL_KEY);
    if (encryptedEmail) {
      return this.cryptoService.decrypt(encryptedEmail);
    }
    return '';
  }

  set savedEmail(value: string) {
    if (value) {
      const encrypted = this.cryptoService.encrypt(value);
      localStorage.setItem(this.SAVED_EMAIL_KEY, encrypted);
    } else {
      localStorage.removeItem(this.SAVED_EMAIL_KEY);
    }
  }

  // ==================== AUTH TOKEN ====================

  get token(): string {
    // Prioriza sessionStorage (sessão atual), depois localStorage (persistente)
    let encryptedToken = sessionStorage.getItem(this.TOKEN_KEY);
    if (encryptedToken) {
      return this.cryptoService.decrypt(encryptedToken);
    }

    encryptedToken = localStorage.getItem(this.TOKEN_KEY);
    if (encryptedToken) {
      return this.cryptoService.decrypt(encryptedToken);
    }

    return '';
  }

  set token(value: string) {
    if (value) {
      const encrypted = this.cryptoService.encrypt(value);
      const useLocalStorage = this.rememberMe;

      if (useLocalStorage) {
        // Salva no localStorage (persistente)
        localStorage.setItem(this.TOKEN_KEY, encrypted);
        sessionStorage.removeItem(this.TOKEN_KEY); // Remove do sessionStorage se existir
      } else {
        // Salva no sessionStorage (sessão temporária)
        sessionStorage.setItem(this.TOKEN_KEY, encrypted);
        localStorage.removeItem(this.TOKEN_KEY); // Remove do localStorage se existir
      }
    } else {
      localStorage.removeItem(this.TOKEN_KEY);
      sessionStorage.removeItem(this.TOKEN_KEY);
    }
  }

  // ==================== REFRESH TOKEN ====================

  get refreshToken(): string {
    // Prioriza sessionStorage (sessão atual), depois localStorage (persistente)
    let encryptedToken = sessionStorage.getItem(this.REFRESH_TOKEN_KEY);
    if (encryptedToken) {
      return this.cryptoService.decrypt(encryptedToken);
    }

    encryptedToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);
    if (encryptedToken) {
      return this.cryptoService.decrypt(encryptedToken);
    }

    return '';
  }

  set refreshToken(value: string) {
    if (value) {
      const encrypted = this.cryptoService.encrypt(value);
      const useLocalStorage = this.rememberMe;

      if (useLocalStorage) {
        // Salva no localStorage (persistente)
        localStorage.setItem(this.REFRESH_TOKEN_KEY, encrypted);
        sessionStorage.removeItem(this.REFRESH_TOKEN_KEY);
      } else {
        // Salva no sessionStorage (sessão temporária)
        sessionStorage.setItem(this.REFRESH_TOKEN_KEY, encrypted);
        localStorage.removeItem(this.REFRESH_TOKEN_KEY);
      }
    } else {
      localStorage.removeItem(this.REFRESH_TOKEN_KEY);
      sessionStorage.removeItem(this.REFRESH_TOKEN_KEY);
    }
  }

  // ==================== USER DATA ====================

  get user(): any {
    // Prioriza sessionStorage (sessão atual), depois localStorage (persistente)
    let encryptedUser = sessionStorage.getItem(this.USER_KEY);
    if (encryptedUser) {
      try {
        const decrypted = this.cryptoService.decrypt(encryptedUser);
        if (decrypted) {
          return JSON.parse(decrypted);
        }
      } catch (e) {
        if (environment.enableDebugLogs) {
          console.error('[LocalStorageService] Erro ao recuperar usuário do sessionStorage:', e);
        }
        sessionStorage.removeItem(this.USER_KEY);
      }
    }

    encryptedUser = localStorage.getItem(this.USER_KEY);
    if (encryptedUser) {
      try {
        const decrypted = this.cryptoService.decrypt(encryptedUser);
        if (decrypted) {
          return JSON.parse(decrypted);
        }
      } catch (e) {
        if (environment.enableDebugLogs) {
          console.error('[LocalStorageService] Erro ao recuperar usuário do localStorage:', e);
        }
        localStorage.removeItem(this.USER_KEY);
      }
    }

    return null;
  }

  set user(value: any) {
    if (value) {
      const userString = JSON.stringify(value);
      const encrypted = this.cryptoService.encrypt(userString);
      const useLocalStorage = this.rememberMe;

      if (useLocalStorage) {
        // Salva no localStorage (persistente)
        localStorage.setItem(this.USER_KEY, encrypted);
        sessionStorage.removeItem(this.USER_KEY);
      } else {
        // Salva no sessionStorage (sessão temporária)
        sessionStorage.setItem(this.USER_KEY, encrypted);
        localStorage.removeItem(this.USER_KEY);
      }
    } else {
      localStorage.removeItem(this.USER_KEY);
      sessionStorage.removeItem(this.USER_KEY);
    }
  }

  // ==================== TRUSTED DEVICE TOKEN ====================

  get trustedDeviceToken(): string {
    // Sempre em localStorage (sobrevive ao logout normal)
    const encrypted = localStorage.getItem(this.TRUSTED_DEVICE_KEY);
    if (encrypted) {
      return this.cryptoService.decrypt(encrypted);
    }
    return '';
  }

  set trustedDeviceToken(value: string) {
    if (value) {
      const encrypted = this.cryptoService.encrypt(value);
      localStorage.setItem(this.TRUSTED_DEVICE_KEY, encrypted);
    } else {
      localStorage.removeItem(this.TRUSTED_DEVICE_KEY);
    }
  }

  // ==================== GENERIC METHODS ====================

  /**
   * Armazena um item criptografado
   */
  setItem(key: string, value: string): void {
    if (!key || !value) return;
    const encrypted = this.cryptoService.encrypt(value);
    localStorage.setItem(key, encrypted);
  }

  /**
   * Recupera um item criptografado
   */
  getItem(key: string): string {
    if (!key) return '';
    const encrypted = localStorage.getItem(key);
    if (encrypted) {
      return this.cryptoService.decrypt(encrypted);
    }
    return '';
  }

  /**
   * Armazena um objeto JSON criptografado
   */
  setObject<T>(key: string, value: T): void {
    if (!key || value === null || value === undefined) return;
    try {
      const jsonString = JSON.stringify(value);
      this.setItem(key, jsonString);
    } catch (e) {
      if (environment.enableDebugLogs) {
        console.error('[LocalStorageService] Erro ao salvar objeto:', e);
      }
    }
  }

  /**
   * Recupera um objeto JSON criptografado
   */
  getObject<T>(key: string): T | null {
    if (!key) return null;
    try {
      const jsonString = this.getItem(key);
      if (jsonString) {
        return JSON.parse(jsonString) as T;
      }
    } catch (e) {
      if (environment.enableDebugLogs) {
        console.error('[LocalStorageService] Erro ao recuperar objeto:', e);
      }
    }
    return null;
  }

  /**
   * Remove um item do storage
   */
  removeItem(key: string): void {
    localStorage.removeItem(key);
  }

  /**
   * Verifica se uma chave existe
   */
  hasItem(key: string): boolean {
    return localStorage.getItem(key) !== null;
  }

  /**
   * Limpa dados de autenticação (logout)
   */
  clear(): void {
    // Limpa de ambos os storages
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(this.TOKEN_KEY);
    sessionStorage.removeItem(this.USER_KEY);
    sessionStorage.removeItem(this.REFRESH_TOKEN_KEY);
  }

  /**
   * Limpa todos os dados do localStorage
   */
  clearAll(): void {
    localStorage.clear();
  }
}
